import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import moment from 'moment';
import { withSnackbar } from 'notistack';
import {
	setPageHeaderTextActionCreator,
	setReportsGranularityActionCreator,
} from 'plugins/store/reducers/common.reducer';
import {
	getReportsByName,
	getRegistrationsTotalCounts,
	downloadReportCsvByName,
	reportsNames,
} from 'services/report.service';
import { canByLicensing } from 'utils/licensing/licensingChecker';
import chartDataMapping from 'mappings/reports';
import { downloadChartsPdf } from 'services/download-file.service';
// @material-ui
import PieChartOutlinedIcon from '@material-ui/icons/PieChartOutlined';
import PieChartIcon from '@material-ui/icons/PieChart';

// core components
import SnackMessage from 'components/Snackbar/SnackMessage';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import CardHeader from 'components/Card/CardHeader';
import CardIcon from 'components/Card/CardIcon';
import ReportController from './ReportController';
import ReportChart from './ReportChart';
import Timeline from '@material-ui/icons/Timeline';
import NumberActiveRegistrations from './NumberActiveRegistrations';

const chartsWrapperParams = {
	[reportsNames.registrations]: {
		title: 'Number of registered Customers during the current date /hour',
		headerIcon: (
			<CardIcon color='info'>
				<Timeline />
			</CardIcon>
		)
	},
	[reportsNames.goodCallsCount]: {
		title: 'Total number of good calls (Android)',
		headerIcon: (
			<CardIcon color='success'>
				<Timeline />
			</CardIcon>
		)
	},
	[reportsNames.goodCallsAnsweredCount]: {
		title: 'Total number of good calls answered (Android)',
		headerIcon: (
			<CardIcon color='warning'>
				<Timeline />
			</CardIcon>
		)
	},
	[reportsNames.goodCallsAnswerSuccessRate]: {
		title: 'Good Call - Answer Success rate % (Android)',
		headerIcon: (
			<CardIcon color='info'>
				<Timeline />
			</CardIcon>
		)
	},
	[reportsNames.spoofedCallsCountWithCustomers]: {
		title: 'Total # of spoofed calls + Total # of Unique Mobiles receiving spoofed calls (Android)',
		headerIcon: (
			<CardIcon
				color="rose"
				style={{ backgroundColor: '#9728B2' }}
			>
				<Timeline />
			</CardIcon>
		)
	},
	[reportsNames.spoofedCallsNotAnsweredSuccessRate]: {
		title: 'Spoofed call Success % (Android)',
		headerIcon: (
			<CardIcon color='info'>
				<Timeline />
			</CardIcon>
		)
	},
	[reportsNames.customersUnderAttackCount]: {
		title: 'Total Number of Customers receiving more than 10 spoofed Calls/day (Android)',
		onlyGranularityByDay: true,
		headerIcon: (
			<CardIcon color='info'>
				<Timeline />
			</CardIcon>
		)
	},
	[reportsNames.devicesDistributionRatio]: {
		title: 'iOS vs Android distribution',
		hidePeriodRange: true,
		headerIcon: (
			<CardIcon
				color="rose"
				style={{ backgroundColor: '#df1962' }}
			>
				<PieChartIcon />
			</CardIcon>
		)
	},
	[reportsNames.ratingsDistribution]: {
		title: 'Ratings distribution',
		headerIcon: (
			<CardIcon color='info'>
				<Timeline />
			</CardIcon>
		)
	},
};

let timerId;

const getReportModel = (chartType) => ({
	chartData: {
		hasData: false,
	},
	from: '',
	to: '',
	loading: false,
	chartType,
});

class Reports extends React.Component {
	viewName = 'Dashboards & Analytics';

	state = {
		contactCenter: '',
		contactCenterName: 'All',
		totalRegistrationsCount: null,
		totalRegistrationsLoading: false,
		reports: {
			[reportsNames.registrations]: getReportModel('line'),
			[reportsNames.goodCallsCount]: getReportModel('line'),
			[reportsNames.goodCallsAnsweredCount]: getReportModel('line'),
			[reportsNames.goodCallsAnswerSuccessRate]: getReportModel('line'),
			[reportsNames.spoofedCallsCountWithCustomers]: getReportModel('bar'),
			[reportsNames.spoofedCallsNotAnsweredSuccessRate]: getReportModel('line'),
			[reportsNames.customersUnderAttackCount]: getReportModel('line'),
			[reportsNames.devicesDistributionRatio]: getReportModel('pie'),
			[reportsNames.ratingsDistribution]: canByLicensing('rating.rateCall') ? getReportModel('bar') : null,
		},
		globalFrom: '',
		globalTo: '',
	};

	reportCharts = React.createRef();

	componentDidMount() {
		this.getAllReports();
		this.props.setPageHeaderText(this.viewName);
	}

	componentWillUnmount() {
		clearTimeout(timerId);
		this.props.setPageHeaderText('');
	}

	setGranularity = (granularity) => {
		this.props.setReportsGranularity(granularity);
	}

	setContactCenter = (contactCenter, contactCenterName) => {
		this.setState({ contactCenter, contactCenterName });
	}

	downloadPdf = (name) => {
		const charts = this.reportCharts.current.querySelectorAll('.j-report-chart');
		const chartsArray = Array.from(charts);

		let chartsData = chartsArray.map((chart) => {
			const reportName = chart.dataset.name;
			const legend = this.state.reports[reportName].chartData.customLegend;
			return {
				element: chart,
				reportName,
				title: chartsWrapperParams[reportName].title,
				dateFrom: this.state.reports[reportName].from || this.state.globalFrom,
				dateTo: this.state.reports[reportName].to || this.state.globalTo,
				granularity: this.getGranularityByReportName(reportName),
				contactCenterName: this.state.contactCenterName,
				hidePeriodRange: chartsWrapperParams[reportName].hidePeriodRange,
				legend,
			};
		});
		if (name) {
			chartsData = chartsData.filter(({ reportName }) => reportName === name);
		}
		return downloadChartsPdf(chartsData);
	}

	downloadCsv = async (name) => {
		try {
			const reportTitle = (chartsWrapperParams[name] && chartsWrapperParams[name].title) || name;
			await downloadReportCsvByName(this.getRequestParamsByName(name), reportTitle);
		} catch (error) {
			this.props.enqueueSnackbar(error.resolvedErrorMessage, {
				content: (key, message) => <SnackMessage id={key} message={message} color='danger' />,
			})
		}
	}

	getAllReports = () => {
		const { updateInterval } = this.props;

		this.getTotalRegistrationsCount();

		const getData = () => {
			Object.keys(this.state.reports).forEach((reportName) => {
				if (this.state.reports[reportName]) {
					this.getReportsByName(reportName);
				}
			});

			clearTimeout(timerId);
			timerId = setTimeout(this.getAllReports, updateInterval * 1000);
		};

		const globalFrom = moment().subtract(6, 'day').startOf('day').toDate();
		const globalTo = moment().endOf('day').toDate();

		if (this.state.globalFrom.toString() !== globalFrom.toString()
			|| this.state.globalTo.toString() !== globalTo.toString()) {
			this.setState({ globalFrom, globalTo }, getData);
		} else {
			getData();
		}
	}

	getTotalRegistrationsCount = async () => {
		try {
			this.setState({ totalRegistrationsLoading: true });
			const { totalRegistrationsCount } = await getRegistrationsTotalCounts();
			this.setState({ totalRegistrationsCount });
		} catch (error) {
			console.log(error);
		}
		this.setState({ totalRegistrationsLoading: false });
	}

	setLoadingByReportName = (name, loading) => {
		this.setState((prevState) => ({
			reports: {
				...prevState.reports,
				[name]: {
					...prevState.reports[name],
					loading,
				},
			},
		}));
	};

	getRequestParamsByName = (name) => {
		const { from, to } = this.setDateRangeByGranularity({
			from: this.state.reports[name].from,
			to: this.state.reports[name].to,
		}, name);
		const contactCenterId = this.state.contactCenter === 'all' ? '' : this.state.contactCenter;

		return {
			name,
			from,
			to,
			contactCenterId,
			granularity: this.getGranularityByReportName(name),
		};
	}

	getGranularityByReportName = (name) => {
		return (chartsWrapperParams[name] && chartsWrapperParams[name].onlyGranularityByDay)
			? 'day'
			: this.props.granularity;
	}

	getReportsByName = async (name) => {
		this.setLoadingByReportName(name, true);
		try {
			const granularity = this.getGranularityByReportName(name);
			const response = await getReportsByName(this.getRequestParamsByName(name));
			const chartData = chartDataMapping[name]((response.data || response), granularity);

			this.setState((prevState) => ({
				reports: {
					...prevState.reports,
					[name]: {
						...prevState.reports[name],
						chartData,
						loading: false,
					},
				},
			}));
		} catch (error) {
			this.setLoadingByReportName(name, false);
			console.log(error);
		}
	}

	setDateRangeByGranularity = (reportPeriod = {}, name) => {
		const granularity = this.getGranularityByReportName(name);
		if (granularity === 'hour') {
			return {
				from: moment().subtract(1, 'day').toDate(),
				to: new Date(),
			}
		} else {
			return {
				from: moment(reportPeriod.from || this.state.globalFrom).startOf('day'),
				to: moment(reportPeriod.to || this.state.globalTo).endOf('day'),
			}
		}
	}

	setDateRangeToReport = async (name, partOfPeriod = 'from', value) => {
		this.setState((prevState) => ({
			reports: {
				...prevState.reports,
				[name]: {
					...prevState.reports[name],
					[partOfPeriod]: value,
				},
			},
		}), () => {
			this.getReportsByName(name);
		});
	}

	getHiddenStatusByReportName = (reportName) => {
		switch (reportName) {
			case reportsNames.customersUnderAttackCount:
				return this.props.granularity === 'hour';
			default:
				return false;
		}
	};

	render() {
		const { contactCenter } = this.state;
		const { granularity } = this.props;

		return (
			<GridContainer>
				<GridItem xs={12}>
					<Card>
						<CardHeader color="primary" icon>
							<CardIcon color="primary">
								<PieChartOutlinedIcon />
							</CardIcon>
						</CardHeader>
						<CardBody>
							<ReportController
								granularity={granularity}
								contactCenter={contactCenter}
								setGranularity={this.setGranularity}
								setContactCenter={this.setContactCenter}
								downloadAll={this.downloadPdf}
								getReports={this.getAllReports}
							/>
							<NumberActiveRegistrations
								count={this.state.totalRegistrationsCount}
								loading={this.state.totalRegistrationsLoading}
							/>
							<div ref={this.reportCharts}>
								<GridContainer>
									{Object.values(reportsNames).map((reportsName, index) => {
										if (!this.state.reports[reportsName]) return null;
										if (this.getHiddenStatusByReportName(reportsName)) return null;
										return (
											<GridItem
												key={index}
												xs={12}
												sm={12}
												md={12}
												lg={6}
												className="j-report-chart"
												data-name={reportsName}
											>
												<ReportChart
													showPeriodRange={this.getGranularityByReportName(reportsName) === 'day' && !chartsWrapperParams[reportsName].hidePeriodRange}
													chartType={this.state.reports[reportsName].chartType}
													getReport={() => this.getReportsByName(reportsName)}
													chartData={this.state.reports[reportsName].chartData}
													loading={this.state.reports[reportsName].loading}
													setDateRangeToReport={(...args) => this.setDateRangeToReport(reportsName, ...args)}
													dateFrom={this.state.reports[reportsName].from || this.state.globalFrom}
													dateTo={this.state.reports[reportsName].to || this.state.globalTo}
													title={chartsWrapperParams[reportsName].title}
													headerIcon={chartsWrapperParams[reportsName].headerIcon}
													downloadCsv={() => this.downloadCsv(reportsName)}
													downloadPdf={() => this.downloadPdf(reportsName)}
													granularity={this.getGranularityByReportName(reportsName)}
													contactCenterName={this.state.contactCenterName}
													hidePeriodRange={chartsWrapperParams[reportsName].hidePeriodRange}
													reportName={reportsName}
												/>
											</GridItem>
										);
									})}
								</GridContainer>
							</div>
						</CardBody>
					</Card>
				</GridItem>
			</GridContainer>
		)
	}
}

const mapStateToProps = ({ common, settings }) => ({
	granularity: common.reportsGranularity,
	updateInterval: settings.refreshAnalyticsDataIntervalInSeconds,
});

const mapDispatchToProps = (dispatch) => ({
	setPageHeaderText: bindActionCreators(
		setPageHeaderTextActionCreator,
		dispatch),
	setReportsGranularity: bindActionCreators(
		setReportsGranularityActionCreator,
		dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(withSnackbar(Reports));
